import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { BriefcaseBusiness, RefreshCw, Upload, X } from "lucide-react";
import { submitInquiry } from "../../../utils/submitInquiry";

const RecruitmentModalContext = createContext(null);

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  linkedin: "",
  position: "",
  captcha: "",
  terms: false,
  resume: null,
};

function createCaptcha() {
  const first = Math.floor(Math.random() * 8) + 1;
  const second = Math.floor(Math.random() * 8) + 1;
  return { question: `${first} + ${second}`, answer: String(first + second) };
}

export function useRecruitmentModal() {
  const context = useContext(RecruitmentModalContext);

  if (!context) {
    throw new Error(
      "useRecruitmentModal must be used within RecruitmentModalProvider",
    );
  }

  return context;
}

function RecruitmentModal({ isOpen, position, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState(createCaptcha);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setForm({ ...EMPTY_FORM, position });
      setErrors({});
      setStatus("");
      setCaptcha(createCaptcha());
    }
  }, [isOpen, position]);

  const inputClassName = (field) =>
    `mt-2 w-full rounded-xl border bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:bg-white focus:ring-2 ${
      errors[field]
        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
        : "border-slate-200 focus:border-[#041E42] focus:ring-[#041E42]/15"
    }`;

  const refreshCaptcha = () => {
    setCaptcha(createCaptcha());
    setForm((current) => ({ ...current, captcha: "" }));
    setErrors((current) => ({ ...current, captcha: "" }));
  };

  const handleChange = ({ target }) => {
    const { name, type, checked, value } = target;
    const nextValue =
      name === "phone" ? value.replace(/[^0-9+\-()\s]/g, "") : value;

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : nextValue,
    }));
    setErrors((current) => ({ ...current, [name]: "" }));
    setStatus("");
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.firstName.trim())
      nextErrors.firstName = "First name is required.";
    if (!form.lastName.trim()) nextErrors.lastName = "Last name is required.";

    const phoneDigits = form.phone.replace(/\D/g, "");
    if (!phoneDigits) nextErrors.phone = "Phone number is required.";
    else if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      nextErrors.phone = "Enter a valid phone number.";
    }

    if (!form.email.trim()) nextErrors.email = "Email address is required.";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!form.resume) nextErrors.resume = "Please upload your resume or CV.";
    else if (!/\.(pdf|doc|docx)$/i.test(form.resume.name)) {
      nextErrors.resume = "Upload a PDF, DOC, or DOCX file.";
    }

    if (!form.position.trim())
      nextErrors.position = "Position applied for is required.";
    if (!form.captcha.trim()) nextErrors.captcha = "Captcha is required.";
    else if (form.captcha.trim() !== captcha.answer)
      nextErrors.captcha = "Captcha does not match.";
    if (!form.terms)
      nextErrors.terms = "Please accept the terms and conditions.";

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) return;

    setIsSubmitting(true);
    setStatus("");

    try {
      await submitInquiry("recruitment", {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        linkedin: form.linkedin.trim(),
        position: form.position.trim(),
        resume: form.resume,
      });
      setStatus("success");
    } catch {
      setStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-resume-title"
        className="max-h-[calc(100vh-2rem)] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/10 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-5 py-5 sm:px-7">
          <div>
            <div className="flex items-center gap-2 text-red-500">
              <BriefcaseBusiness size={19} aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-[0.2em]">
                Careers
              </span>
            </div>
            <h2
              id="send-resume-title"
              className="mt-2 text-2xl font-bold text-[#041E42] sm:text-3xl"
            >
              Send Resume
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-[#041E42]"
            aria-label="Close send resume form"
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 px-5 py-6 sm:px-7 sm:py-7"
        >
          {status === "success" && (
            <p
              role="status"
              className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
            >
              Thank you. Your resume submission has been sent to our recruitment
              team.
            </p>
          )}
          {status === "error" && (
            <p
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              We could not submit your resume. Please try again.
            </p>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="First Name"
              error={errors.firstName}
              htmlFor="resume-first-name"
              required
            >
              <input
                id="resume-first-name"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                className={inputClassName("firstName")}
                autoComplete="given-name"
              />
            </Field>
            <Field
              label="Last Name"
              error={errors.lastName}
              htmlFor="resume-last-name"
              required
            >
              <input
                id="resume-last-name"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                className={inputClassName("lastName")}
                autoComplete="family-name"
              />
            </Field>
            <Field
              label="Phone Number"
              error={errors.phone}
              htmlFor="resume-phone"
              required
            >
              <input
                id="resume-phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                className={inputClassName("phone")}
                autoComplete="tel"
              />
            </Field>
            <Field
              label="Email Address"
              error={errors.email}
              htmlFor="resume-email"
              required
            >
              <input
                id="resume-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className={inputClassName("email")}
                autoComplete="email"
              />
            </Field>
            <Field label="LinkedIn Profile" htmlFor="resume-linkedin">
              <input
                id="resume-linkedin"
                name="linkedin"
                type="url"
                value={form.linkedin}
                onChange={handleChange}
                className={inputClassName("linkedin")}
                placeholder="https://linkedin.com/in/your-profile"
              />
            </Field>
            <Field
              label="Upload Resume/CV"
              error={errors.resume}
              htmlFor="resume-upload"
              required
            >
              <label
                htmlFor="resume-upload"
                className={`mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed px-4 py-3 text-sm transition hover:border-red-400 hover:bg-red-50/50 ${errors.resume ? "border-red-500" : "border-slate-300"}`}
              >
                <Upload
                  size={18}
                  className="shrink-0 text-red-500"
                  aria-hidden="true"
                />
                <span className="truncate text-slate-600">
                  {form.resume ? form.resume.name : "Choose PDF, DOC, or DOCX"}
                </span>
              </label>
              <input
                id="resume-upload"
                name="resume"
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="sr-only"
                onChange={({ target }) => {
                  const resume = target.files?.[0] ?? null;
                  setForm((current) => ({ ...current, resume }));
                  setErrors((current) => ({ ...current, resume: "" }));
                }}
              />
            </Field>
          </div>

          <Field
            label="Position Applied For"
            error={errors.position}
            htmlFor="resume-position"
            required
          >
            <input
              id="resume-position"
              name="position"
              value={form.position}
              onChange={handleChange}
              className={inputClassName("position")}
            />
          </Field>

          <Field
            label="Captcha"
            error={errors.captcha}
            htmlFor="resume-captcha"
            required
          >
            <div className="mt-2 flex gap-2">
              <input
                id="resume-captcha"
                name="captcha"
                value={form.captcha}
                onChange={handleChange}
                className={inputClassName("captcha").replace("mt-2 ", "")}
                inputMode="numeric"
              />
              <span
                className="flex min-w-24 items-center justify-center rounded-xl bg-[#041E42] px-3 text-sm font-semibold text-white"
                aria-label={`Captcha question: ${captcha.question}`}
              >
                {captcha.question}
              </span>
              <button
                type="button"
                onClick={refreshCaptcha}
                className="rounded-xl border border-slate-200 px-3 text-[#041E42] transition hover:bg-slate-50"
                aria-label="Refresh captcha"
              >
                <RefreshCw size={18} aria-hidden="true" />
              </button>
            </div>
          </Field>

          <div>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-600">
              <input
                name="terms"
                type="checkbox"
                checked={form.terms}
                onChange={handleChange}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <span>
                I agree to the Terms &amp; Conditions and consent to SAC
                processing my application.
              </span>
            </label>
            {errors.terms && (
              <p className="mt-2 text-sm text-red-600">{errors.terms}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="group relative w-full overflow-hidden rounded-full bg-red-600 px-6 py-3.5 font-semibold text-white transition hover:-translate-y-0.5 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="relative z-10">
              {isSubmitting ? "Submitting…" : "Submit Resume"}
            </span>
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-700 group-hover:translate-x-full" />
          </button>
        </form>
      </section>
    </div>,
    document.body,
  );
}

function Field({ children, error, htmlFor, label, required = false }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-sm font-semibold text-[#041E42]">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export function RecruitmentModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState("");

  const value = useMemo(
    () => ({
      openRecruitmentModal: (nextPosition = "") => {
        setPosition(nextPosition);
        setIsOpen(true);
      },
    }),
    [],
  );

  return (
    <RecruitmentModalContext.Provider value={value}>
      {children}
      <RecruitmentModal
        isOpen={isOpen}
        position={position}
        onClose={() => setIsOpen(false)}
      />
    </RecruitmentModalContext.Provider>
  );
}
